'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import type { SidebarResize } from '../context';
const KEYBOARD_STEP = 16;
type Drag = {
  startWidth: number;
  startX: number;
};

/**
 * Pointer and keyboard behaviour for a sidebar's resize handle. Pass `resize` only while the
 * handle is rendered. The handle is a direct child of the sidebar container, so the container's
 * width is the sidebar's rendered width.
 */
function useSidebarResize(side, resize) {
  const $ = _c(30);
  const [drag, setDrag] = React.useState(null);
  const [focused, setFocused] = React.useState(false);
  const [width, setWidth] = React.useState();
  if (!resize && (drag || focused)) {
    setDrag(null);
    setFocused(false);
  }
  if (!resize) {
    return null;
  }
  const {
    maxWidth,
    minWidth,
    onResize
  } = resize;
  const direction = side === "right" ? -1 : 1;
  let t0;
  if ($[0] !== minWidth) {
    t0 = handle => handle.parentElement?.getBoundingClientRect().width ?? minWidth;
    $[0] = minWidth;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const containerWidth = t0;
  let t1;
  if ($[2] !== maxWidth || $[3] !== minWidth || $[4] !== onResize) {
    t1 = nextWidth => onResize(Math.round(Math.min(maxWidth, Math.max(minWidth, nextWidth))));
    $[2] = maxWidth;
    $[3] = minWidth;
    $[4] = onResize;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const resizeTo = t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setDrag(null);
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const endDrag = t2;
  let t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setFocused(false);
    t4 = () => setFocused(true);
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== containerWidth || $[10] !== direction || $[11] !== resizeTo) {
    t5 = event => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }
      event.preventDefault();
      const keyDirection = event.key === "ArrowRight" ? 1 : -1;
      resizeTo(containerWidth(event.currentTarget) + keyDirection * direction * KEYBOARD_STEP);
    };
    $[9] = containerWidth;
    $[10] = direction;
    $[11] = resizeTo;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== containerWidth) {
    t6 = event_0 => {
      if (event_0.button !== 0) {
        return;
      }
      event_0.preventDefault();
      event_0.currentTarget.focus();
      event_0.currentTarget.setPointerCapture(event_0.pointerId);
      setDrag({
        startWidth: containerWidth(event_0.currentTarget),
        startX: event_0.clientX
      });
    };
    $[13] = containerWidth;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== direction || $[16] !== drag || $[17] !== resizeTo) {
    t7 = event_1 => {
      if (drag) {
        resizeTo(drag.startWidth + (event_1.clientX - drag.startX) * direction);
      }
    };
    $[15] = direction;
    $[16] = drag;
    $[17] = resizeTo;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = handle_0 => {
      const container = handle_0?.parentElement;
      if (!container) {
        return;
      }
      const observer = new ResizeObserver(t9 => {
        const [entry] = t9;
        const size = entry?.borderBoxSize[0];
        if (size) {
          setWidth(Math.round(size.inlineSize));
        }
      });
      observer.observe(container);
      return () => observer.disconnect();
    };
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== maxWidth || $[21] !== minWidth || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== width) {
    t9 = {
      "aria-orientation": "vertical",
      "aria-valuemax": maxWidth,
      "aria-valuemin": minWidth,
      "aria-valuenow": width,
      onBlur: t3,
      onFocus: t4,
      onKeyDown: t5,
      onLostPointerCapture: endDrag,
      onPointerCancel: endDrag,
      onPointerDown: t6,
      onPointerMove: t7,
      onPointerUp: endDrag,
      ref: t8,
      role: "separator",
      tabIndex: 0
    };
    $[20] = maxWidth;
    $[21] = minWidth;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = width;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== focused || $[28] !== t9) {
    t10 = {
      handleProps: t9,
      resizing: focused
    };
    $[27] = focused;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10 as const;
}
export { useSidebarResize };